import { Modal, Button, Heading, Text, Radio, useToast } from '@afterpaytouch/core'
import React, { FunctionComponent, useCallback, useState } from 'react'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { useTranslation } from 'next-i18next'
import { useLocalStorage } from '@afterpay/utils/storage'
import { useConsumerSelector, useUpdateAccountDetailMutation } from '../../state'
import { SupportedLocale } from '@afterpay/types'

const I18N_NAMESPACE = ['common', 'account']

const testNameSpace = 'edit-language'

interface EditLanguageModalProps {
  isModalOpen: boolean
  closeModal: () => void
}

export const EditLanguageModal: FunctionComponent<EditLanguageModalProps> = ({ isModalOpen, closeModal }) => {
  const { t } = useTranslation(I18N_NAMESPACE)

  const locales: Array<{ value: SupportedLocale; label: string }> = [
    { value: 'en-CA', label: t('account:edit:language:content:en-CA') },
    { value: 'fr-CA', label: t('account:edit:language:content:fr-CA') },
  ]

  const consumer = useConsumerSelector()

  const [newPreferredLocale, setNewPreferredLocale] = useState<SupportedLocale>(consumer.preferredLocale as SupportedLocale)
  const [updateAccountDetails, { isLoading: isUpdateAccountDetailsLoading }] = useUpdateAccountDetailMutation()
  const [_, setPreferredLocaleToLocalStorage] = useLocalStorage('preferredLocale', consumer.preferredLocale)

  const amplitude = useAmplitudeWithEnduringEventProperties()
  const setToast = useToast()

  const onShow = (): void => amplitude.logEvent(TrackingEvent.CLICKED_EDIT_LANGUAGE)

  const handleSelect = (locale: SupportedLocale): void => setNewPreferredLocale(locale)

  const isSubmitDisabled = newPreferredLocale === consumer.preferredLocale || isUpdateAccountDetailsLoading

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (isSubmitDisabled) {
      return
    }

    try {
      await updateAccountDetails({
        givenNames: consumer.givenNames,
        otherGivenNames: consumer.otherGivenNames,
        surname: consumer.surname,
        dateOfBirth: consumer.dateOfBirth,
        address1: consumer.contactAddress?.address1 ?? '',
        address2: consumer.contactAddress?.address2 ?? '',
        area2: consumer.contactAddress?.area2 ?? '',
        countryCode: consumer.countryCode,
        postcode: consumer.contactAddress?.postcode ?? '',
        state: consumer.contactAddress?.state ?? '',
        suburb: consumer.contactAddress?.suburb ?? '',
        creditPolicyAgree: false,
        preferredLocale: newPreferredLocale,
      }).unwrap()

      setPreferredLocaleToLocalStorage(newPreferredLocale)

      amplitude.logEvent(TrackingEvent.EDIT_LANGUAGE_SUCCESS, { from: consumer.preferredLocale as SupportedLocale, to: newPreferredLocale })

      const currentUrl = new URL(window.location.href)
      const pathSegments = currentUrl.pathname.split('/')
      pathSegments[1] = newPreferredLocale
      currentUrl.pathname = pathSegments.join('/')
      window.location.href = currentUrl.toString()
    } catch (e) {
      amplitude.logEvent(TrackingEvent.EDIT_LANGUAGE_ERROR, { from: consumer.preferredLocale as SupportedLocale, to: newPreferredLocale })
      setToast({
        message: t('common:error:defaultErrorMsg'),
        kind: 'Failure',
      })
    }
  }, [
    isSubmitDisabled,
    updateAccountDetails,
    consumer.givenNames,
    consumer.otherGivenNames,
    consumer.surname,
    consumer.dateOfBirth,
    consumer.contactAddress?.address1,
    consumer.contactAddress?.address2,
    consumer.contactAddress?.area2,
    consumer.contactAddress?.postcode,
    consumer.contactAddress?.state,
    consumer.contactAddress?.suburb,
    consumer.countryCode,
    consumer.preferredLocale,
    newPreferredLocale,
    setPreferredLocaleToLocalStorage,
    amplitude,
    setToast,
    t,
  ])

  if (!isModalOpen) {
    return null
  }

  return (
    <Modal
      show={isModalOpen}
      onShow={onShow}
      onClose={closeModal}
      onHide={closeModal}
      onBackdropClick={closeModal}
      onEscapeKeyDown={closeModal}
      testNameSpace={testNameSpace}
    >
      <Modal.Header divider>
        <div className='text-center'>
          <Heading testNameSpace={testNameSpace} size='L'>
            {t('account:edit:language:header:heading')}
          </Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <Radio.Group
          value={newPreferredLocale}
          testNameSpace={testNameSpace}
          id={testNameSpace}
          name={testNameSpace}
          icon='Select'
          kind='Option'
          onChange={(e) => handleSelect(e.target.value as SupportedLocale)}
        >
          {locales.map((locale) => (
            <Radio.Button
              value={locale.value}
              label={locale.label}
              key={`${testNameSpace}-[${locale.value}]`}
              testNameSpace={`${testNameSpace}-[${locale.value}]`}
              disabled={isUpdateAccountDetailsLoading}
            />
          ))}
        </Radio.Group>
      </Modal.Content>
      <Modal.Footer>
        <Button padding='Fluid' disabled={isSubmitDisabled} loading={isUpdateAccountDetailsLoading} onClick={handleSubmit} testNameSpace={testNameSpace}>
          <Text bold>{t('account:edit:language:footer:cta')}</Text>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
